import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';
import { getPosts } from '../lib/sanity';
import '../styles/Blog.css';

const BlogPostSkeleton = () => (
  <div className="blog-card skeleton">
    <div className="skeleton-image"></div>
    <div className="blog-content">
      <div className="skeleton-title"></div>
      <div className="skeleton-text"></div>
      <div className="skeleton-text"></div>
    </div>
  </div>
);

const BlogPostCard = ({ post }) => {
  if (!post) return null;
  
  const date = new Date(post.publishedAt).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <Link to={`/blog/${post.slug}`} className="blog-card">
      {post.mainImage && post.mainImage.asset && (
        <div 
          className="blog-image"
          style={{
            backgroundImage: `url(${post.mainImage.asset.url})`
          }}
          role="img"
          aria-label={post.title}
        />
      )}
      <div className="blog-content">
        <h2>{post.title}</h2>
        <div className="blog-meta">
          {date} · {post.estimatedReadingTime || 1} min read
        </div>
        {post.excerpt && <p className="blog-excerpt">{post.excerpt}</p>}
        <div className="blog-link">
          Read more
        </div>
      </div>
    </Link>
  );
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useMetaTags({
    title: 'Blog | Sam Penny - SEO Specialist',
    description: 'Read my latest thoughts and insights on SEO, digital marketing, and web development.',
  });

  const loadPosts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedPosts = await getPosts();
      setPosts(fetchedPosts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setError(error.message || 'Failed to load blog posts');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    loadPosts();
  };

  return (
    <div className="blog-page">
      <h1>Blog</h1>
      {isLoading ? (
        <div className="blog-grid">
          {[...Array(4)].map((_, index) => (
            <BlogPostSkeleton key={index} />
          ))}
        </div>
      ) : error ? (
        <div className="error-container">
          <h2 className="error-title">Oops! Something went wrong</h2>
          <p className="error-message">{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Try Again
          </button>
        </div>
      ) : posts.length === 0 ? (
        <div className="no-posts">
          <h2 className="no-posts-title">No posts yet</h2>
          <p className="no-posts-message">Check back soon for new content!</p>
        </div>
      ) : (
        <div className="blog-grid">
          {posts.map(post => (
            <BlogPostCard key={post._id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Blog; 